import { ref, readonly, computed } from "vue";
import type { Message, GpuTrainingResults, GpuAiResult } from "~/types/gpu";
import type { GpuCalculationStrategy } from "~/composables/useGpuStrategies";
import {
  createLocalCalculationStrategy,
  fetchGpuAiQuery,
} from "~/composables/useGpuStrategies";
import { adjustGpuResults } from "~/composables/useGpuAdjustment";
import { useClusterBuildingStateMachine } from "~/composables/useClusterBuildingStateMachine";

export const useAiChat = (calculationStrategy?: GpuCalculationStrategy) => {
  const { t } = useI18n();

  const RECOMMENDATION_CONFIG = {
    targetGpuType: 'G4',
    maxResults: 4,
    defaultTrainingHours: 24,
    billingType: 'month' as const,
    promote: true
  };

  const clusterStateMachine = useClusterBuildingStateMachine();

  const createInitialMessages = (): Message[] => [
    {
      content: "",
      type: "assistant",
      timestamp: new Date(),
      messageKey: "aiChat.welcomeDescription",
    },
  ]

  const messages = ref<Message[]>([
    {
      content: "",
      type: "assistant",
      timestamp: new Date(),
      messageKey: "aiChat.welcomeDescription",
    },
  ]);

  const isTyping = ref(false);
  const isLoading = ref(false);
  const strategy = calculationStrategy || createLocalCalculationStrategy();
  const conversationVersion = ref(0)
  const pendingTimers = new Set<ReturnType<typeof setTimeout>>()

  // Computed: 判斷當前是否允許空輸入
  const allowEmptyInput = computed(() => {
    const optionalStates = ['askRequirements', 'askDuration', 'askProject']
    return clusterStateMachine.isActive.value && 
           optionalStates.includes(clusterStateMachine.currentState.value as string)
  })

  const generateSimpleResponse = async (): Promise<string> => {
    return t("aiChat.noOptionsAvailable");
  };

  const processGpuQuery = async (
    userMessage: string
  ): Promise<{
    content: string;
    gpuResults?: GpuTrainingResults;
    aiResults?: GpuAiResult[];
    messageKey?: string;
    messageParams?: any;
    formStep?: string;
    awaitingInput?: boolean;
  }> => {
    try {
      isLoading.value = true;

      const trimmedMessage = userMessage.trim().toLowerCase();
      
      const clusterBuildingKeywords = [
        'start building your cluster',
        'start building cluster',
        'build cluster',
        'cluster building',
        '開始建立叢集',
        '建立叢集',
        'construir cluster',
        'comenzar a construir cluster'
      ];
      
      if (clusterBuildingKeywords.some(keyword => trimmedMessage.includes(keyword))) {
        clusterStateMachine.transition('askName')
        return {
          content: t('clusterBuilding.askName'),
          messageKey: 'clusterBuilding.askName',
          formStep: 'askName',
          awaitingInput: true
        }
      }

      const recommendationKeywords = [
        '推薦', 'recommend', 'recommendation', 
        'promote', 'promotion', '促銷',
        'promoción', 'promociones', 'promover'
      ];
      
      if (recommendationKeywords.includes(trimmedMessage)) {
        const cartState = useCartState();
        
        // 確保 stock 數據已載入
        if (cartState.stock.length === 0) {
          try {
            await cartState.getStock();
          } catch (error) {
            console.warn('無法載入 stock 數據，將使用 API 查詢');
            // 如果無法載入 stock，繼續使用 API 查詢
          }
        }
        console.log(cartState.stock);
        // 從 stock 中選擇推薦商品（找 planName 中包含指定 GPU 類型的商品，有庫存的，限制數量）
        const recommendedProducts = cartState.stock
          .filter(item => 
            item.planName.includes(RECOMMENDATION_CONFIG.targetGpuType) && 
            item.left > 0
          )
          .slice(0, RECOMMENDATION_CONFIG.maxResults);
        
        console.log(`推薦 ${RECOMMENDATION_CONFIG.targetGpuType} 商品：`, recommendedProducts)
        
        if (recommendedProducts.length > 0) {
          // 將 Stock 格式轉換為 GpuAiResult 格式
          const recommendedResults: GpuAiResult[] = recommendedProducts.map(stock => ({
            product_id: stock.product_id,  
            name: stock.planName,
            gpu_type: stock.gpu_type,
            gpu_number: 1, // 預設為 1，因為 Stock 類型中沒有這個欄位
            stock_quantity: stock.left,
            training_hours: RECOMMENDATION_CONFIG.defaultTrainingHours,
            total_cost: RECOMMENDATION_CONFIG.billingType === 'month' && RECOMMENDATION_CONFIG.promote 
              ? Number(stock.monthly_fee) 
              : Number(stock.hourly_fee) * RECOMMENDATION_CONFIG.defaultTrainingHours,
            calculate_result: 'first', // 標記為推薦商品
            billing_type: RECOMMENDATION_CONFIG.billingType, // 從配置設定計費類型
            promote: RECOMMENDATION_CONFIG.promote
          }));

          return {
            content: t("aiChat.recommendationResult"),
            aiResults: recommendedResults,
            messageKey: "aiChat.recommendationResult"
          };
        }
      }

      // 嘗試使用 API 查詢(可能拿到完整 response)
      try {
        const apiResponse = await fetchGpuAiQuery(userMessage);

        // 根據 status_code 和 messageKey 處理
        const { status_code, messageKey, gpuResults, messageParams } =
          apiResponse;

        // Status 1: 成功,返回結果
        if (status_code === 1 && gpuResults && gpuResults.length > 0) {
          const adjustedResults = adjustGpuResults(gpuResults);
          return {
            content:
              messageKey === "aiChat.insufficientBudget"
                ? ""
                : messageParams
                ? t(messageKey, messageParams)
                : t(messageKey),
            messageKey,
            messageParams,
            aiResults: adjustedResults,
          };
        }

        // Status 4: 預算不足，只更新 required_budget，不顯示卡片
        if (status_code === 4 && gpuResults && gpuResults.length > 0) {
          const adjustedResults = adjustGpuResults(gpuResults);
          // 提取調整後的金額作為最低預算要求
          const requiredBudget =
            adjustedResults[0]?.total_cost ||
            apiResponse.budget_info?.required_budget;

          return {
            content: "",
            messageKey,
            messageParams: {
              ...messageParams,
              required: Number(requiredBudget).toLocaleString(),
            },
            // 不返回 aiResults，所以不會顯示 GPU 卡片
          };
        }

        // Status 2, 3: 使用 messageKey 和 messageParams 翻譯訊息
        return {
          content:
            messageKey === "aiChat.insufficientBudget"
              ? ""
              : messageParams
              ? t(messageKey, messageParams)
              : t(messageKey),
          messageKey,
          messageParams,
        };
      } catch (apiError) {
        // API 查詢失敗,嘗試使用 fallback strategy (可能是 local 計算)
        console.log("API 查詢失敗,使用 fallback strategy:", apiError);
        const results = await strategy(userMessage);

        // 判斷結果格式
        if (Array.isArray(results)) {
          // AI API 格式 - 調整結果使訓練時間為整數小時
          const adjustedResults = adjustGpuResults(results);
          return {
            content: t("aiChat.calculationResult"),
            aiResults: adjustedResults,
          };
        } else {
          // 本地計算格式
          return {
            content: t("aiChat.calculationResult"),
            gpuResults: results,
          };
        }
      }
    } catch (error: any) {
      console.error(t("aiChat.calculationError"), error);
      // 最終 fallback
      return {
        content: t("aiChat.noOptionsAvailable", {
          email: "sales.ai@amaryllo.us",
        }),
      };
    } finally {
      isLoading.value = false;
    }
  };

  const addMessage = (
    content: string,
    type: "user" | "assistant",
    gpuResults?: GpuTrainingResults,
    aiResults?: GpuAiResult[],
    messageKey?: string,
    messageParams?: any,
    formStep?: string,
    awaitingInput?: boolean
  ) => {
    messages.value.push({
      content,
      type,
      timestamp: new Date(),
      gpuResults,
      aiResults,
      messageKey,
      messageParams,
      formStep,
      awaitingInput,
    });
  };

  const scheduleAssistantMessage = (
    version: number,
    delay: number,
    callback: () => void
  ) => {
    const timer = setTimeout(() => {
      pendingTimers.delete(timer)
      if (version !== conversationVersion.value) return
      callback()
    }, delay)
    pendingTimers.add(timer)
  }

  const resetConversationRuntime = () => {
    conversationVersion.value += 1
    pendingTimers.forEach((timer) => clearTimeout(timer))
    pendingTimers.clear()
    isTyping.value = false
    isLoading.value = false
  }

  const processClusterBuildingInput = async (userInput: string) => {
    const { 
      currentState, 
      formData, 
      transition, 
      validateCurrentStep, 
      saveStepData, 
      nextStep,
      transitionToEditState,
      isConfirmState,
      stateToFieldMap,
      isEditingFromConfirm,
      resetEditFlag
    } = clusterStateMachine
    
    const trimmedInput = userInput.trim()
    const lowerInput = trimmedInput.toLowerCase()
    
    const cancelKeywords = ['cancel', 'stop', 'quit', '取消', '停止', 'cancelar', 'detener']
    if (cancelKeywords.includes(lowerInput)) {
      transition('cancelled')
      clusterStateMachine.reset()
      return {
        content: t('clusterBuilding.cancelled'),
        messageKey: 'clusterBuilding.cancelled'
      }
    }
    
    if (isConfirmState.value) {
      const confirmKeywords = ['confirm', 'yes', 'submit', 'ok', '確認', '提交', 'sí', 'confirmar']
      if (confirmKeywords.includes(lowerInput)) {
        transition('submitting')
        
        try {
          // 先保存 email，因為 reset 會清空 formData
          const userEmail = formData.value.email
          
          await $fetch('/api/cluster-inquiry', {
            method: 'POST',
            body: formData.value
          })
          
          transition('completed')
          clusterStateMachine.reset()
          
          return {
            content: t('clusterBuilding.success', { email: userEmail }),
            messageKey: 'clusterBuilding.success',
            messageParams: { email: userEmail }
          }
        } catch (error) {
          console.error('Cluster inquiry submission error:', error)
          transition('confirm')
          return {
            content: t('clusterBuilding.submitError'),
            messageKey: 'clusterBuilding.submitError',
            messageParams: formData.value,  // ✅ 加入表單資料
            formStep: 'confirm',
            awaitingInput: true
          }
        }
      }
      
      const editState = transitionToEditState(trimmedInput)
      if (editState) {
        transition(editState)
        const fieldName = stateToFieldMap[editState]
        const currentValue = fieldName ? formData.value[fieldName] : ''
        
        return {
          content: t(`clusterBuilding.${editState}`) + 
                   (currentValue ? `\n\n💡 ${t('clusterBuilding.currentValue')}: ${currentValue}` : ''),
          messageKey: `clusterBuilding.${editState}`,
          formStep: editState,
          awaitingInput: true
        }
      }
      
      return {
        content: t('clusterBuilding.confirmHelp'),
        messageKey: 'clusterBuilding.confirmHelp',
        formStep: 'confirm',
        awaitingInput: true
      }
    }
    
    const validation = validateCurrentStep(trimmedInput)
    if (!validation.valid) {
      return {
        content: validation.error || t('clusterBuilding.validationError'),
        messageKey: 'clusterBuilding.validationError',
        formStep: currentState.value,
        awaitingInput: true
      }
    }
    
    saveStepData(trimmedInput)
    
    if (isEditingFromConfirm.value) {
      resetEditFlag()
      transition('confirm')
      return {
        content: t('clusterBuilding.confirmSummary', formData.value),
        messageKey: 'clusterBuilding.confirmSummary',
        messageParams: formData.value,
        formStep: 'confirm',
        awaitingInput: true
      }
    }
    
    const next = nextStep()
    
    if (next === 'confirm') {
      return {
        content: t('clusterBuilding.confirmSummary', formData.value),
        messageKey: 'clusterBuilding.confirmSummary',
        messageParams: formData.value,
        formStep: 'confirm',
        awaitingInput: true
      }
    }
    
    if (next) {
      return {
        content: t(`clusterBuilding.${next}`),
        messageKey: `clusterBuilding.${next}`,
        formStep: next,
        awaitingInput: true
      }
    }
    
    return {
      content: t('clusterBuilding.error'),
      messageKey: 'clusterBuilding.error'
    }
  };

  const sendMessage = async (userMessage: string): Promise<void> => {
    // Allow empty input only when in optional cluster building fields
    if (!userMessage.trim() && !allowEmptyInput.value) return;

    const activeVersion = conversationVersion.value
    addMessage(userMessage, "user");
    isTyping.value = true;

    try {
      if (clusterStateMachine.isActive.value) {
        const response = await processClusterBuildingInput(userMessage)
        if (activeVersion !== conversationVersion.value) return
        scheduleAssistantMessage(activeVersion, 800, () => {
          addMessage(
            response.content,
            "assistant",
            undefined,
            undefined,
            response.messageKey,
            response.messageParams,
            response.formStep,
            response.awaitingInput
          )
          isTyping.value = false
        })
        return
      }

      try {
        const response = await processGpuQuery(userMessage);
        if (activeVersion !== conversationVersion.value) return
        scheduleAssistantMessage(
          activeVersion,
          1000 + Math.random() * 1000,
          () => {
            addMessage(
              response.content,
              "assistant",
              response.gpuResults,
              response.aiResults,
              response.messageKey,
              response.messageParams,
              response.formStep,
              response.awaitingInput
            );
            isTyping.value = false;
          }
        )
      } catch (gpuError) {
        const simpleResponse = await generateSimpleResponse();
        if (activeVersion !== conversationVersion.value) return
        scheduleAssistantMessage(
          activeVersion,
          1000 + Math.random() * 1000,
          () => {
            addMessage(simpleResponse, "assistant");
            isTyping.value = false;
          }
        )
      }
    } catch (error) {
      console.error("發送訊息錯誤:", error);
      if (activeVersion !== conversationVersion.value) return
      scheduleAssistantMessage(activeVersion, 1000, () => {
        addMessage(t("aiChat.sendError"), "assistant");
        isTyping.value = false;
      })
    }
  };

  const clearMessages = () => {
    resetConversationRuntime()
    clusterStateMachine.reset()
    messages.value = createInitialMessages()
  };

  const cancelClusterBuilding = () => {
    if (!clusterStateMachine.isActive.value) return
    resetConversationRuntime()
    clusterStateMachine.reset()
    messages.value.push({
      content: t('clusterBuilding.cancelled'),
      type: 'assistant',
      timestamp: new Date(),
      messageKey: 'clusterBuilding.cancelled',
    })
  };

  return {
    messages: readonly(messages),
    isTyping: readonly(isTyping),
    isLoading: readonly(isLoading),
    allowEmptyInput: readonly(allowEmptyInput),
    isClusterBuilding: clusterStateMachine.isActive,
    sendMessage,
    clearMessages,
    cancelClusterBuilding,
  };
};
